import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { screenPath, type RouteName } from '@commerce/core'
import { YALO_GRADIENT_AI, YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { CartGlyph } from './icons'
import { articlesText } from '../text'

interface Props {
  slug: string
  sessionId: string
  /** Drawn as a badge on the cart tab. `0` shows no badge. */
  cartCount?: number
  /** When given, the design's centre "Promos" button opens the reels. */
  onOpenStories?: () => void
}

const iconSx = {
  width: 22,
  height: 22,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

const ICONS: Record<'home' | 'orders' | 'cart' | 'account', ReactNode> = {
  home: (
    <>
      <path d="m3 10.5 9-7.5 9 7.5V20a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 20Z" />
      <path d="M9.5 21.5v-7h5v7" />
    </>
  ),
  orders: (
    <>
      <path d="M6 2.5h12a1 1 0 0 1 1 1V21l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21V3.5a1 1 0 0 1 1-1Z" />
      <path d="M9 8h6M9 12h4" />
    </>
  ),
  cart: <CartGlyph />,
  account: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c1.5-3.5 4.2-5 7.5-5s6 1.5 7.5 5" />
    </>
  ),
}

/**
 * The bottom navigation (reference.html:1194–1222).
 *
 * The four destinations are `NavLink`s, not click handlers: they are navigation,
 * so they belong in the accessibility tree as links, they open in a new tab on
 * middle-click like any other link, and React Router sets `aria-current="page"`
 * on the active one — which is what tells a screen reader where it is, rather
 * than the design's colour change alone.
 *
 * The centre "Promos" control is the exception. It opens an overlay on the
 * current screen and goes nowhere, so it is a button; it renders only when the
 * screen passes a handler for it.
 */
export const BottomNav = ({ slug, sessionId, cartCount = 0, onOpenStories }: Props) => {
  const tabSx = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '3px',
    py: 0.5,
    textDecoration: 'none',
    color: YALO_SEMANTIC.fg3,
    '&[aria-current="page"]': { color: YALO_SEMANTIC.fg1 },
  } as const

  interface Tab {
    name: RouteName
    label: string
    icon: keyof typeof ICONS
    /** The catalog lives at the session root, so it matches every child route. */
    end?: boolean
  }

  const tab = (entry: Tab) => (
    <Box
      key={entry.name}
      component={NavLink}
      to={screenPath(entry.name, { slug, sessionId })}
      end={entry.end}
      sx={tabSx}
      {...(entry.name === 'cart' && cartCount > 0
        ? { 'aria-label': `Carrito, ${articlesText(cartCount)}` }
        : {})}
    >
      <Box sx={{ position: 'relative' }}>
        <Box component="svg" viewBox="0 0 24 24" sx={iconSx}>
          {ICONS[entry.icon]}
        </Box>
        {entry.name === 'cart' && cartCount > 0 && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -4,
              right: -10,
              minWidth: 16,
              height: 16,
              px: '4px',
              borderRadius: `${YALO_RADIUS.pill}px`,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
            }}
          >
            {cartCount}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>{entry.label}</Typography>
    </Box>
  )

  return (
    <Stack
      component="nav"
      aria-label="Navegación principal"
      direction="row"
      alignItems="flex-end"
      sx={{
        // `sticky` alone is not enough: it pins the nav only while the content
        // overflows, so on a short screen — an order list with four rows — the
        // nav sits wherever the content ended, halfway up the page. `mt: auto`
        // in the column flex `Screen` provides pushes it to the bottom when
        // there is spare room, and does nothing when there is not.
        mt: 'auto',
        position: 'sticky',
        bottom: 0,
        zIndex: 3,
        px: 1.5,
        // 12px top and bottom; the env() term only adds on devices with a home
        // indicator, so a desktop browser gets exactly 12px.
        pt: 1.5,
        pb: 'calc(12px + env(safe-area-inset-bottom))',
        bgcolor: 'rgba(255, 255, 255, 0.86)',
        backdropFilter: 'blur(20px) saturate(140%)',
        borderTop: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
      }}
    >
      {tab({ name: 'catalog', label: 'Inicio', icon: 'home', end: true })}
      {tab({ name: 'orders', label: 'Pedidos', icon: 'orders' })}

      {onOpenStories && (
        <ButtonBase
          onClick={onOpenStories}
          aria-label="Ver promos"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '3px',
            mt: -1.5,
            color: YALO_SEMANTIC.fg3,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: `${YALO_RADIUS.pill}px`,
              background: YALO_GRADIENT_AI,
              border: '2px solid #fff',
              boxShadow: '0 4px 12px rgba(215, 93, 255, 0.32)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:active': { transform: 'scale(0.94)' },
            }}
          >
            <Box component="svg" viewBox="0 0 24 24" width={16} height={16} fill="#fff">
              <path d="M8 5v14l11-7Z" />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>Promos</Typography>
        </ButtonBase>
      )}

      {tab({ name: 'cart', label: 'Carrito', icon: 'cart' })}
      {tab({ name: 'account', label: 'Cuenta', icon: 'account' })}
    </Stack>
  )
}
